"use client"

import consola from "consola"

import { brand } from "@/lib/brand"

import Image from "next/image"
import Link from "next/link"
import { useEffect } from "react-safe"
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react"

import { Button } from "@/components/ui/button"

const supportEmail = brand.supportEmail
const diagnosticHints = [
  "Your secure session may have expired please sign in again if prompted.",
  "The record you requested may have been moved or archived.",
  "We might be experiencing a temporary connectivity issue."
]

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && process.env.NEXT_RUNTIME !== "edge") {
      consola.error("[Error]", error)
    }
  }, [error])

  const formatErrorMessage = (message: string) => {
    if (!message) {
      return "An unexpected fault was reported."
    }

    return message.replaceAll("$", "\n$")
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="relative w-full max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div
          className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-destructive/20 blur-3xl"
          aria-hidden="true"
        />

        <div className="relative grid gap-10 px-10 py-12">
          <header className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Image
                  src={brand.logoPath}
                  alt={`${brand.shortName} emblem`}
                  width={48}
                  height={48}
                  className="rounded-md border border-primary/30 object-cover"
                  priority
                />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {brand.organization}
                </p>
                <p className="text-lg font-semibold text-primary">{brand.shortName} incident centre</p>
              </div>
            </div>

            <span className="rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-destructive">
              System alert
            </span>
          </header>

          <section className="grid gap-6">
            <div className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-background/60 p-6">
              <div className="flex items-center gap-3 text-destructive">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-6 w-6" aria-hidden="true" />
                </span>
                <div>
                  <h2 className="text-xl font-semibold text-destructive">A system issue occurred</h2>
                  <p className="text-sm text-muted-foreground">
                    The platform hit an unexpected condition while processing your request.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-muted/30 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Incident details</p>
                <pre className="mt-3 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-background/80 p-3 text-xs font-mono text-muted-foreground">
                  {formatErrorMessage(error.message || "")}
                </pre>
                <p className="mt-4 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Reference:</span> {error.digest || "Not available"}
                </p>
              </div>
            </div>

            <div className="grid gap-3 rounded-2xl border border-border/60 bg-primary/5 p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Diagnostic hints</p>
              <ul className="grid gap-2 text-sm text-muted-foreground">
                {diagnosticHints.map((hint) => (
                  <li key={hint} className="flex items-start gap-2">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-primary" aria-hidden="true" />
                    <span>{hint}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <div className="flex flex-col gap-4 border-t border-border pt-6 text-sm text-muted-foreground">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                If the issue persists, contact the digital operations team and quote the reference number.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" asChild className="gap-2 border-primary/50 text-primary hover:bg-primary/10">
                  <Link href="/">
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    Return to home
                  </Link>
                </Button>
                <Button onClick={() => reset()} className="gap-2 bg-primary hover:bg-primary/90">
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  Try again
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-background/80 p-4">
              <div className="flex items-center gap-3 text-foreground">
                <Image src="/images/analyze.svg" alt="Support icon" width={32} height={32} className="h-8 w-8" />
                <div>
                  <p className="font-medium text-foreground">Digital operations desk</p>
                  <p>
                    <a href={`mailto:${supportEmail}`} className="text-primary hover:underline">
                      {supportEmail}
                    </a>
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">We monitor incidents 24/7 to keep services stable.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}











